import { Field, useFormikContext } from 'formik';
import get from 'lodash/get';
import type { ReactElement, ReactNode } from 'react';

import FormErrorMessage from '@tmh-website/components/shared/FormErrorMessage';
import { Color } from '@tmh-website/shared/colors';
import { FormElementCommonProps, SelectOption, StyledFormElements } from '@tmh-website/ui/FormElements';
import Radio from '@tmh-website/ui/Radio';

export interface RadioGroupProps extends FormElementCommonProps {
  /**
   * The width of a checkbox in a grid layout
   */
  gridMinWidth?: string;
  options: SelectOption<ReactNode>[];
  otherRadioLabel?: string;
  otherRadioValue?: string;
  /**
   * An element that should be shown when the other field is selected
   */
  otherOption?: ReactElement | HTMLElement;
  helpText?: string | ReactNode;
}

const RadioGroup = ({
  themeColor = Color.Neutral_0_White,
  gridMinWidth,
  helpText,
  label,
  name,
  options,
  otherOption,
  otherRadioLabel = 'Other',
  otherRadioValue = 'other',
  required,
}: RadioGroupProps) => {
  const { errors, touched, values } = useFormikContext<any>();
  const currentSelection = values ? get(values, name) : null;

  const gridStyles = gridMinWidth
    ? {
        gridTemplateColumns: `repeat(auto-fill, minmax(${gridMinWidth}, 1fr)`,
      }
    : undefined;

  const hasError = get(errors, name) && get(touched, name);
  const hasErrorOther = get(errors, otherRadioValue) && get(touched, otherRadioValue);
  const otherOptionIsActive = currentSelection === otherRadioValue;

  return (
    <div>
      {label && (
        <StyledFormElements.Label $themeColor={themeColor} htmlFor={name}>
          {label}
          {required && '*'}
        </StyledFormElements.Label>
      )}
      <StyledFormElements.Group style={gridStyles}>
        {options.map(option => (
          <Field
            as={Radio}
            key={option.value.toString()}
            name={name}
            value={option.value}
            disabled={option.disabled}
            hideErrorMessage
            themeColor={themeColor}>
            {option.label}
          </Field>
        ))}
      </StyledFormElements.Group>
      {otherOption && (
        <StyledFormElements.Group bottomRow style={gridStyles}>
          <Field as={Radio} name={name} value={otherRadioValue} hideErrorMessage themeColor={themeColor}>
            <StyledFormElements.GroupOtherContainer>
              <>
                <StyledFormElements.GroupOtherLabel>{otherRadioLabel}</StyledFormElements.GroupOtherLabel>
                {otherOptionIsActive && otherOption}
              </>
            </StyledFormElements.GroupOtherContainer>
          </Field>
        </StyledFormElements.Group>
      )}
      {helpText && <StyledFormElements.HelpText $themeColor={themeColor}>{helpText}</StyledFormElements.HelpText>}
      {hasError && (
        <StyledFormElements.ErrorMessage>
          <FormErrorMessage name={name} />
        </StyledFormElements.ErrorMessage>
      )}
      {hasErrorOther && (
        <StyledFormElements.ErrorMessage>
          <FormErrorMessage name={otherRadioValue} />
        </StyledFormElements.ErrorMessage>
      )}
    </div>
  );
};

export default RadioGroup;
